import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { 
  Loader2, Mail, Calendar, Clock, MapPin, Bike, CheckCircle, 
  LogOut, User, ChevronRight, RefreshCw, Phone
} from "lucide-react";

// Check if volunteer is logged in
const isVolunteerLoggedIn = () => {
  return !!localStorage.getItem("volunteer_token");
};

// Volunteer Login Page
function VolunteerLogin() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setSending(true);
    try {
      await api.volunteerRequestLogin(email);
      setSent(true);
      toast.success("Check your email for the login link!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send login link");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Check Your Email</h1>
          <p className="text-slate-600 mb-6">
            We've sent a login link to <strong>{email}</strong>. 
            Click the link in the email to access your volunteer portal.
          </p>
          <p className="text-sm text-slate-500">
            The link expires in 1 hour. If you don't see the email, check your spam folder.
          </p>
          <Button 
            variant="outline" 
            className="mt-6"
            onClick={() => setSent(false)}
          >
            Use a different email
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <img src="/favicon.png" alt="DVC" className="w-12 h-12" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800">Volunteer Portal</h1>
          <p className="text-slate-600 mt-2">Dodder Valley Cycling</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Enter your email address
            </label>
            <Input
              type="email"
              placeholder="your.email@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-12"
              required
              data-testid="volunteer-email-input"
            />
          </div>
          <Button 
            type="submit" 
            className="w-full h-12 bg-green-600 hover:bg-green-700 text-white rounded-xl"
            disabled={sending || !email}
            data-testid="volunteer-login-btn"
          >
            {sending ? (
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
            ) : (
              <Mail className="w-5 h-5 mr-2" />
            )}
            Send Login Link
          </Button>
        </form>

        <p className="text-center text-sm text-slate-500 mt-6">
          No password required. We'll send you a secure link.
        </p>
      </div>
    </div>
  );
}

// Token Verification Page
function VolunteerVerify() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying"); // verifying, success, error
  const [error, setError] = useState("");

  useEffect(() => {
    const token = searchParams.get("token");
    if (!token) {
      setStatus("error");
      setError("No login token provided");
      return;
    }

    api.volunteerVerifyLogin(token)
      .then((res) => {
        localStorage.setItem("volunteer_token", res.data.token);
        localStorage.setItem("volunteer_name", res.data.volunteer.name);
        localStorage.setItem("volunteer_email", res.data.volunteer.email);
        setStatus("success");
        setTimeout(() => navigate("/volunteer/dashboard"), 1500);
      })
      .catch((err) => {
        setStatus("error");
        setError(err.response?.data?.detail || "Failed to verify login link");
      });
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        {status === "verifying" && (
          <>
            <Loader2 className="w-12 h-12 animate-spin text-green-600 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-slate-800">Verifying your login...</h1>
          </>
        )}
        {status === "success" && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-xl font-bold text-slate-800">Login Successful!</h1>
            <p className="text-slate-600 mt-2">Redirecting to your dashboard...</p>
          </>
        )}
        {status === "error" && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-xl font-bold text-slate-800">Login Failed</h1>
            <p className="text-red-600 mt-2">{error}</p>
            <Button 
              className="mt-6 bg-green-600 hover:bg-green-700"
              onClick={() => navigate("/volunteer")}
            >
              Request New Login Link
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

// Assignment Card Component
function AssignmentCard({ assignment }) {
  const isPast = new Date(assignment.datetime_iso) < new Date();

  return (
    <div className={`bg-white rounded-xl border ${isPast ? 'border-slate-200 opacity-75' : 'border-green-200'} p-4 mb-3`}>
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Calendar className="w-4 h-4 text-green-600" />
            <span className="font-semibold text-slate-800">{assignment.date}</span>
          </div>
          <div className="flex items-center gap-2 text-slate-600 text-sm mb-2">
            <Clock className="w-4 h-4" />
            <span>{assignment.time} • {assignment.duration_hours}hr</span>
          </div>
          {assignment.meeting_point && (
            <div className="flex items-center gap-2 text-slate-600 text-sm mb-2">
              <MapPin className="w-4 h-4" />
              <span>{assignment.meeting_point}</span>
            </div>
          )}
          <div className="flex items-center gap-2 text-slate-600 text-sm">
            <span>{assignment.bike_assignment || assignment.bike_choice}</span>
          </div>
          {assignment.requester_name && (
            <div className="flex items-center gap-2 text-slate-500 text-sm mt-2">
              <User className="w-4 h-4" />
              <span>Requester: {assignment.requester_name}</span>
            </div>
          )}
          {assignment.requester_phone && (
            <div className="flex items-center gap-2 text-slate-500 text-sm mt-1">
              <Phone className="w-4 h-4" />
              <a href={`tel:${assignment.requester_phone}`} className="text-green-700 hover:underline">
                {assignment.requester_phone}
              </a>
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className={`text-xs px-2 py-1 rounded-full ${
            assignment.status === 'confirmed' ? 'bg-green-100 text-green-700' :
            assignment.status === 'cancelled' ? 'bg-red-100 text-red-700' :
            'bg-yellow-100 text-yellow-700'
          }`}>
            {assignment.status}
          </span>
        </div>
      </div>
    </div>
  );
}

// Available Booking Card
function AvailableBookingCard({ booking, onClaim, claiming }) {
  return (
    <div className="bg-white rounded-xl border border-blue-200 p-4 mb-3">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Calendar className="w-4 h-4 text-blue-600" />
            <span className="font-semibold text-slate-800">{booking.date}</span>
          </div>
          <div className="flex items-center gap-2 text-slate-600 text-sm mb-2">
            <Clock className="w-4 h-4" />
            <span>{booking.time} • {booking.duration_hours}hr</span>
          </div>
          {booking.meeting_point && (
            <div className="flex items-center gap-2 text-slate-600 text-sm">
              <MapPin className="w-4 h-4" />
              <span>{booking.meeting_point}</span>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2">
          {booking.needs_volunteer_for.map((bike) => (
            <Button
              key={bike}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
              onClick={() => onClaim(booking.id, booking.bike_choice === "Trishaw and Wheelchair Bike" ? bike : null)}
              disabled={claiming === `${booking.id}-${bike}`}
            >
              {claiming === `${booking.id}-${bike}` ? (
                <Loader2 className="w-3 h-3 animate-spin mr-1" />
              ) : (
                <Bike className="w-3 h-3 mr-1" />
              )}
              Claim {bike}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}

// Main Dashboard
function VolunteerDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [assignments, setAssignments] = useState({ upcoming: [], past: [] });
  const [available, setAvailable] = useState([]);
  const [activeTab, setActiveTab] = useState("upcoming");
  const [claiming, setClaiming] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const volunteerName = localStorage.getItem("volunteer_name") || "Volunteer";

  const fetchData = async () => {
    try {
      const [profileRes, assignmentsRes, availableRes] = await Promise.all([
        api.volunteerGetProfile(),
        api.volunteerGetAssignments(),
        api.volunteerGetAvailable()
      ]);
      setProfile(profileRes.data);
      setAssignments(assignmentsRes.data);
      setAvailable(availableRes.data.available);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("volunteer_token");
        navigate("/volunteer");
        toast.error("Session expired. Please login again.");
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!isVolunteerLoggedIn()) {
      navigate("/volunteer");
      return;
    }
    fetchData();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("volunteer_token");
    localStorage.removeItem("volunteer_name");
    localStorage.removeItem("volunteer_email");
    navigate("/volunteer");
    toast.success("Logged out successfully");
  };

  const handleClaim = async (bookingId, bikeType) => {
    const bikeLabel = bikeType || "this booking";
    const confirmed = window.confirm(`Are you sure you want to claim ${bikeLabel}? The requester will be notified that you have confirmed.`);
    if (!confirmed) return;
    
    const claimKey = `${bookingId}-${bikeType || 'single'}`;
    setClaiming(claimKey);
    try {
      await api.volunteerClaimBooking(bookingId, bikeType);
      toast.success("Booking claimed successfully!");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to claim booking");
    } finally {
      setClaiming(null);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-green-600 text-white p-4 pb-6">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <img src="/favicon.png" alt="DVC" className="w-6 h-6" />
            <span className="font-semibold">DVC Volunteer</span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-white hover:bg-green-700"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
        <h1 className="text-2xl font-bold">Hi, {volunteerName.split(' ')[0]}!</h1>
      </div>

      {/* Content */}
      <div className="p-4 -mt-2">
        {/* Tabs */}
        <div className="flex bg-white rounded-xl p-1 shadow-sm mb-4">
          <button
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
              activeTab === "upcoming" ? "bg-green-600 text-white" : "text-slate-600"
            }`}
            onClick={() => setActiveTab("upcoming")}
          >
            My Bookings ({assignments.upcoming.length})
          </button>
          <button
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
              activeTab === "available" ? "bg-blue-600 text-white" : "text-slate-600"
            }`}
            onClick={() => setActiveTab("available")}
          >
            Available ({available.length})
          </button>
          <button
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
              activeTab === "history" ? "bg-slate-600 text-white" : "text-slate-600"
            }`}
            onClick={() => setActiveTab("history")}
          >
            History
          </button>
        </div>

        {/* Refresh Button */}
        <div className="flex justify-end mb-3">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleRefresh}
            disabled={refreshing}
            className="text-slate-500"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Content based on tab */}
        {activeTab === "upcoming" && (
          <div>
            {assignments.upcoming.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No upcoming assignments</p>
                <p className="text-sm mt-1">Check the Available tab to claim bookings</p>
              </div>
            ) : (
              assignments.upcoming.map((a) => (
                <AssignmentCard 
                  key={a.id} 
                  assignment={a} 
                />
              ))
            )}
          </div>
        )}

        {activeTab === "available" && (
          <div>
            {available.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Bike className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No bookings need volunteers right now</p>
                <p className="text-sm mt-1">Check back later or wait for email notifications</p>
              </div>
            ) : (
              available.map((b) => (
                <AvailableBookingCard 
                  key={b.id} 
                  booking={b} 
                  onClaim={handleClaim}
                  claiming={claiming}
                />
              ))
            )}
          </div>
        )}

        {activeTab === "history" && (
          <div>
            {assignments.past.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No past assignments yet</p>
              </div>
            ) : (
              assignments.past.map((a) => (
                <AssignmentCard 
                  key={a.id} 
                  assignment={a} 
                />
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Main Export
export default function VolunteerPortal({ page }) {
  if (page === "verify") return <VolunteerVerify />;
  if (page === "dashboard") return <VolunteerDashboard />;
  
  // If logged in and accessing /volunteer, redirect to dashboard
  if (isVolunteerLoggedIn() && page === "login") {
    window.location.href = "/volunteer/dashboard";
    return null;
  }
  
  return <VolunteerLogin />;
}
